import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Slide,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import { useAuth } from "context/AuthContext";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";
import { useSystemTheme } from "context/SystemThemeContext";

// Import components
import TeamScheduling from "./features/TeamScheduling";

// Import hooks
import useGoogleCalendarStatus from "./hooks/useGoogleCalendarStatus";
import useSingleProvider from "./hooks/useSingleProvider";
import useTeamScheduling from "./hooks/useTeamScheduling";
import useGoogleOAuth from "./hooks/useGoogleOAuth";

// Import utils
import { calendarProviders, TEAM_SCHEDULING_PROVIDER, DEFAULT_PROVIDER } from "./utils/providerConfig";
import { validateUrl } from "./utils/validation";
import TeamManagementModal from "./features/TeamScheduling/TeamManagementModal";
import SingleCalendar from "./features/SingleCalendar/Index";

const CalendarIntegration = () => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;

  const [activeTab, setActiveTab] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: "",
    data: null,
  });
  const [teamManagementModalOpen, setTeamManagementModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [isSavingTeamProvider, setIsSavingTeamProvider] = useState(false);
  const [isRemovingAccount, setIsRemovingAccount] = useState(false);

  // Initialize hooks
  const googleStatus = useGoogleCalendarStatus(domainId);
  const singleProvider = useSingleProvider(domainId);
  const teamScheduling = useTeamScheduling(domainId);

  // No onSuccess callback needed — handled by redirect return effect below
  const googleOAuth = useGoogleOAuth(domainId);

  const LOOM_SHARE_URLS = {
    'google-calendar': process.env.REACT_APP_GOOGLE_CALENDAR_LOOM_URL,
    'calendly': process.env.REACT_APP_CALENDLY_LOOM_URL,
    'zoho-calendar': process.env.REACT_APP_ZOHO_CALENDAR_LOOM_URL,
    'team-scheduling': process.env.REACT_APP_TEAM_SCHEDULING_LOOM_URL,
  };

  const getEmbedUrlForProvider = useCallback((providerId) => {
    const shareUrl = LOOM_SHARE_URLS[providerId];
    if (!shareUrl) return null;
    return shareUrl.replace('/share/', '/embed/');
  }, []);

  const providerHasVideo = useCallback((providerId) => {
    return calendarProviders.find(p => p.id === providerId)?.hasVideo && LOOM_SHARE_URLS[providerId];
  }, []);

  // Handle redirect return from Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("oauth") === "success") {
      googleStatus.checkGoogleCredentials();

      if (activeTab === 1 || singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
        teamScheduling.fetchTeamSchedulingStatus();
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: "Google Calendar connected successfully!",
      });

      window.history.replaceState({}, "", window.location.pathname);
    }

    if (params.get("oauth") === "error") {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to connect Google Calendar. Please try again.",
      });

      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Initialize integrations on mount
  useEffect(() => {
    if (domainId) {
      const initializeIntegrations = async () => {
        try {
          await singleProvider.fetchActiveProvider();
          googleStatus.checkGoogleCredentials();
          if (singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
            await teamScheduling.fetchTeamSchedulingStatus();
          }
        } catch (error) {
          console.error('Failed to initialize integrations:', error);
        }
      };

      initializeIntegrations();
    }
  }, [domainId]);

  useEffect(() => {
    if (singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
      setActiveTab(1);
    }
  }, [singleProvider.existingProvider]);

  useEffect(() => {
    if (activeTab === 1 && domainId && googleStatus.hasGoogleCredentials) {
      teamScheduling.fetchTeamSchedulingStatus();
    }
  }, [activeTab, domainId, googleStatus.hasGoogleCredentials]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, []);

  const handleSaveTeamSchedulingProvider = useCallback(async () => {
    setIsSavingTeamProvider(true);
    const result = await singleProvider.saveProvider(TEAM_SCHEDULING_PROVIDER, "");

    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.success ? "Team scheduling enabled!" : result.message,
    });

    if (result.success) {
      setActiveTab(1);
      await teamScheduling.fetchTeamSchedulingStatus();
    }

    setIsSavingTeamProvider(false);
  }, [singleProvider, teamScheduling]);

  const handleRemoveGoogleAccount = useCallback(async () => {
    setConfirmDialog({ open: false, action: "" });
    setIsRemovingAccount(true);

    try {
      const res = await axios.delete(
        `${FAST_API_BASE_URL}/api/mcp-credentials/google-calendar`,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      if (res.data.resultType === "Success" || res.data.resultCode === "0") {
        if (singleProvider.hasExistingConnection) {
          await singleProvider.disconnectProvider();
        }

        googleStatus.setHasGoogleCredentials(false);
        googleStatus.setSingleCalendarStatus(null);
        teamScheduling.setTeamSchedulingStatus(null);
        teamScheduling.setTeamMembers([]);
        teamScheduling.setIsTeamSchedulingEnabled(false);

        setSnackbar({
          open: true,
          severity: "success",
          message: "Google account removed successfully!",
        });

        setActiveTab(0);
      } else {
        throw new Error(res.data.message || "Failed to remove account");
      }
    } catch (err) {
      console.error("Failed to remove Google account:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err.response?.data?.message || "Failed to remove account. Please try again.",
      });
    } finally {
      setIsRemovingAccount(false);
    }
  }, [domainId, singleProvider, googleStatus, teamScheduling]);

  const handleConnectGoogle = useCallback(async () => {
    const result = await googleOAuth.initiateOAuth();
    if (result?.success === false) {
      setSnackbar({
        open: true,
        severity: "error",
        message: result.message || "Failed to connect Google Calendar.",
      });
    }
  }, [googleOAuth]);

  const handleDisconnect = useCallback(async () => {
    const result = await singleProvider.disconnectProvider();
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.message,
    });
    setConfirmDialog({ open: false, action: "" });
  }, [singleProvider]);

  const handleSaveGoogleCalendarProvider = useCallback(async () => {
    if (!googleStatus.hasGoogleCredentials) {
      await handleConnectGoogle();
      return;
    }

    if (!googleStatus.singleCalendarStatus?.is_configured) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please configure your working hours first before activating.",
      });
      return;
    }

    const result = await singleProvider.saveProvider("google-calendar", "google-calendar://configured");
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.success ? "Google Calendar single scheduling activated!" : result.message,
    });
  }, [googleStatus, singleProvider, handleConnectGoogle]);

  const handleSaveProvider = useCallback(async () => {
    if (singleProvider.selectedProvider === TEAM_SCHEDULING_PROVIDER) {
      await handleSaveTeamSchedulingProvider();
      return;
    }

    if (singleProvider.selectedProvider === "google-calendar") {
      await handleSaveGoogleCalendarProvider();
      return;
    }

    if (!singleProvider.selectedProvider || !singleProvider.calendarUrl.trim()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please select a provider and enter a valid URL",
      });
      return;
    }

    const error = validateUrl(singleProvider.selectedProvider, singleProvider.calendarUrl);
    if (error) {
      setUrlError(error);
      return;
    }

    if (singleProvider.hasExistingConnection) {
      setConfirmDialog({
        open: true,
        action: "update",
        data: { newProvider: singleProvider.selectedProvider },
      });
      return;
    }

    const result = await singleProvider.saveProvider(singleProvider.selectedProvider, singleProvider.calendarUrl);
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.message,
    });
  }, [singleProvider, validateUrl, handleSaveTeamSchedulingProvider, handleSaveGoogleCalendarProvider]);

  const handleEnableTeamScheduling = useCallback(async () => {
    if (!googleStatus.hasGoogleCredentials) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please connect Google Calendar first to use team scheduling.",
      });
      return;
    }

    if (!teamScheduling.teamSchedulingStatus?.is_configured) {
      setTeamManagementModalOpen(true);
    } else {
      await handleSaveTeamSchedulingProvider();
    }
  }, [googleStatus.hasGoogleCredentials, teamScheduling.teamSchedulingStatus, handleSaveTeamSchedulingProvider]);

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        p: { xs: 2, md: 3 },
        borderRadius: '16px',
        boxShadow: isDarkMode ? "0px 2px 12px rgba(0,0,0,0.2)" : "0px 2px 12px rgba(0,0,0,0.04)",
        minHeight: "500px",
        width: '100%',
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
    >
      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: "" })}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: 1,
            maxWidth: '90vw',
            width: 'min(500px, 90vw)',
            m: 2,
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "600",
            color: confirmDialog.action === "remove-account" ? "#d32f2f" : "#219297",
            padding: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontSize: '1.1rem',
          }}
        >
          <Info sx={{ color: confirmDialog.action === "remove-account" ? "#d32f2f" : "#ff9800", fontSize: 22 }} />
          {confirmDialog.action === "disconnect" ? "Disconnect?" :
            confirmDialog.action === "update" ? "Update Connection?" :
            confirmDialog.action === "remove-account" ? "Remove Google Account?" :
            "Confirm Action"}
        </DialogTitle>
        <DialogContent sx={{ padding: '8px 16px' }}>
          <DialogContentText sx={{ color: isDarkMode ? "#a0aec0" : "#666", fontSize: '0.9rem', lineHeight: 1.6 }}>
            {confirmDialog.action === "disconnect"
              ? "Are you sure you want to disconnect? This will remove all integration settings."
              : confirmDialog.action === "remove-account"
              ? "Are you sure you want to remove your Google account? This will permanently delete all Google Calendar credentials, disconnect any active integrations, and remove all team scheduling configurations. This action cannot be undone."
              : "You already have a provider connected. Connecting a new one will replace the existing connection."}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '12px 16px',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            onClick={() => setConfirmDialog({ open: false, action: "" })}
            fullWidth={isMobile}
            sx={{
              color: isDarkMode ? "#a0aec0" : "#666",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              padding: '8px 16px',
              fontSize: '0.9rem',
              "&:hover": { backgroundColor: isDarkMode ? "#2d2d3f" : "#f5f5f5" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.action === "disconnect") {
                handleDisconnect();
              } else if (confirmDialog.action === "update") {
                singleProvider
                  .saveProvider(singleProvider.selectedProvider, singleProvider.calendarUrl)
                  .then(result => {
                    setSnackbar({
                      open: true,
                      severity: result.success ? "success" : "error",
                      message: result.message,
                    });
                    setConfirmDialog({ open: false, action: "" });
                  });
              } else if (confirmDialog.action === "remove-account") {
                handleRemoveGoogleAccount();
              }
            }}
            variant="contained"
            fullWidth={isMobile}
            disabled={isRemovingAccount}
            startIcon={isRemovingAccount ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            sx={{
              backgroundColor: confirmDialog.action === "remove-account" ? "#d32f2f" : "#219297",
              fontWeight: 600,
              textTransform: "none",
              color: "#fff",
              borderRadius: "8px",
              padding: '8px 16px',
              fontSize: '0.9rem',
              "&:hover": {
                backgroundColor: confirmDialog.action === "remove-account" ? "#b71c1c" : "#1a767a",
              },
            }}
          >
            {isRemovingAccount ? "Removing..." :
              confirmDialog.action === "disconnect" ? "Disconnect" :
              confirmDialog.action === "remove-account" ? "Remove Account" :
              "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            backgroundColor:
              snackbar.severity === "success" ? "#219297" :
              snackbar.severity === "error" ? "#d32f2f" :
              snackbar.severity === "info" ? "#1976d2" : "#219297",
            color: "#fff",
            borderRadius: '10px',
            fontSize: '0.9rem',
            maxWidth: '90vw',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <SoftBox mb={3}>
        <SoftBox display="flex" alignItems="center" gap={2} mb={1}>
          <Box
            sx={{
              borderRadius: '10px',
              p: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={AnseraLogo}
              alt="Ansera Logo"
              style={{ 
                width: '48px', 
                height: 'auto',
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          </Box>
          <SoftTypography
            variant="h5"
            fontWeight="bold"
            sx={{ color: isDarkMode ? "#e2e8f0" : "dark", fontSize: { xs: '1.25rem', md: '1.5rem' } }}
          >
            Scheduling Integration
          </SoftTypography>
        </SoftBox>
        <SoftTypography
          variant="body2"
          sx={{ color: isDarkMode ? "#a0aec0" : "text", opacity: 0.7, fontSize: '0.9rem' }}
        >
          Connect your calendar or setup team scheduling
        </SoftTypography>
      </SoftBox>

      {/* Tabs */}
{/* Tabs */}
<Box
  sx={{
    borderBottom: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
    mb: 3,
  }}
>
  <Tabs
    value={activeTab}
    onChange={handleTabChange}
    sx={{
      backgroundColor: isDarkMode ? '#060C18 !important' : '#fff !important',
      minHeight: '48px',
      '& .MuiTabs-scroller': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiTabs-flexContainer': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiTab-root': {
        backgroundColor: 'transparent !important',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: { xs: '0.9rem', md: '1rem' },
        minWidth: { xs: 'auto', md: 160 },
        padding: { xs: '12px 8px', md: '12px 16px' },
        color: isDarkMode ? '#ffffff !important' : '#666 !important',
        '&.Mui-selected': { 
          color: '#219297 !important',
        },
      },
    }}
    TabIndicatorProps={{
      sx: {
        backgroundColor: '#219297',
        height: '3px',
      }
    }}
  >
    <Tab label="Single Calendar" />
    <Tab label="Team Scheduling" />
  </Tabs>
</Box>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <SingleCalendar
          domainId={domainId}
          singleProvider={singleProvider}
          googleStatus={googleStatus}
          googleOAuth={googleOAuth}
          snackbar={snackbar}
          setSnackbar={setSnackbar}
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
          showVideo={showVideo}
          setShowVideo={setShowVideo}
          currentVideoUrl={currentVideoUrl}
          setCurrentVideoUrl={setCurrentVideoUrl}
          urlError={urlError}
          setUrlError={setUrlError}
          isRemovingAccount={isRemovingAccount}
          isMobile={isMobile}
          LOOM_SHARE_URLS={LOOM_SHARE_URLS}
          validateUrl={validateUrl}
          handleSaveProvider={handleSaveProvider}
          handleConnectGoogle={handleConnectGoogle}
          handleSaveGoogleCalendarProvider={handleSaveGoogleCalendarProvider}
          handleRemoveGoogleAccount={handleRemoveGoogleAccount}
        />
      ) : (
        <TeamScheduling
          domainId={domainId}
          singleProvider={singleProvider}
          googleStatus={googleStatus}
          teamScheduling={teamScheduling}
          snackbar={snackbar}
          setSnackbar={setSnackbar}
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
          showVideo={showVideo}
          setShowVideo={setShowVideo}
          currentVideoUrl={currentVideoUrl}
          setCurrentVideoUrl={setCurrentVideoUrl}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          savingSettings={savingSettings}
          setSavingSettings={setSavingSettings}
          teamManagementModalOpen={teamManagementModalOpen}
          setTeamManagementModalOpen={setTeamManagementModalOpen}
          isSavingTeamProvider={isSavingTeamProvider}
          setIsSavingTeamProvider={setIsSavingTeamProvider}
          isRemovingAccount={isRemovingAccount}
          isMobile={isMobile}
          LOOM_SHARE_URLS={LOOM_SHARE_URLS}
          handleConnectGoogle={handleConnectGoogle}
          handleSaveTeamSchedulingProvider={handleSaveTeamSchedulingProvider}
          handleRemoveGoogleAccount={handleRemoveGoogleAccount}
          handleEnableTeamScheduling={handleEnableTeamScheduling}
        />
      )}

      {/* Team Management Modal */}
      {teamManagementModalOpen && (
        <TeamManagementModal
          open={teamManagementModalOpen}
          onClose={() => {
            setTeamManagementModalOpen(false);
            teamScheduling.fetchTeamSchedulingStatus();
          }}
          domainId={domainId}
          refreshData={teamScheduling.fetchTeamSchedulingStatus}
          initialTeamData={teamScheduling.teamMembers}
        />
      )}
    </SoftBox>
  );
};

export default CalendarIntegration;